import { useEffect, useRef, useState, useCallback } from "react"

const BreadcrumbDropdown = ({ items, activeItemId, isOpen, onClose, onSelect, anchorRef }) => {
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const listRef = useRef(null)
  const dropdownRef = useRef(null)

  // Reset highlight when opening, toggle body class for toast z-index
  // Skip when fw-toast.payroll is visible so it stays above the header
  useEffect(() => {
    if (isOpen) {
      setHighlightIndex(-1)

      const hasPayrollToast = document.querySelector(".fw-toast.payroll")
      if (!hasPayrollToast) {
        document.body.classList.add("breadcrumb-dropdown-open")
      }

      // Scroll active item into view
      setTimeout(() => {
        const activeEl = listRef.current?.querySelector(".breadcrumb-dropdown-item.active")
        if (activeEl) {
          activeEl.scrollIntoView({ block: "nearest" })
        }
      }, 0)
    } else {
      document.body.classList.remove("breadcrumb-dropdown-open")
    }

    return () => document.body.classList.remove("breadcrumb-dropdown-open")
  }, [isOpen])

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        anchorRef?.current &&
        !anchorRef.current.contains(e.target)
      ) {
        onClose()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isOpen, onClose, anchorRef])

  // Close on Escape, arrow key navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onClose()
        return
      }

      if (e.key === "ArrowDown") {
        e.preventDefault()
        setHighlightIndex((prev) => (prev < items.length - 1 ? prev + 1 : prev))
      } else if (e.key === "ArrowUp") {
        e.preventDefault()
        setHighlightIndex((prev) => (prev > 0 ? prev - 1 : prev))
      } else if (e.key === "Enter" && highlightIndex >= 0 && highlightIndex < items.length) {
        e.preventDefault()
        const hi = items[highlightIndex]
        onSelect({ ...hi, isActive: hi.id === activeItemId })
      }
    },
    [items, highlightIndex, onClose, onSelect]
  )

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIndex >= 0) {
      const items = listRef.current?.querySelectorAll(".breadcrumb-dropdown-item")
      items?.[highlightIndex]?.scrollIntoView({ block: "nearest" })
    }
  }, [highlightIndex])

  if (!isOpen) return null

  return (
    <div className="breadcrumb-dropdown" ref={dropdownRef} onKeyDown={handleKeyDown}>
      <div className="breadcrumb-dropdown-list" ref={listRef}>
        {items.map((item, index) => (
          <div
            key={item.id}
            className={`breadcrumb-dropdown-item${item.id === activeItemId ? " active" : ""}${index === highlightIndex ? " highlighted" : ""}`}
            onClick={() => onSelect({ ...item, isActive: item.id === activeItemId })}
          >
            {item.name}
          </div>
        ))}
      </div>
    </div>
  )
}

export default BreadcrumbDropdown
